/**
 * MixpanelService
 * Core wrapper for Mixpanel analytics with privacy-first design
 */

import mixpanel from 'mixpanel-browser';
import { MIXPANEL_CONFIG } from '../config/mixpanelConfig';
import privacyAnonymizer from './PrivacyAnonymizer';

// Persists a decline that happens before Mixpanel finishes initializing,
// so initialize() can apply it before any queued events flush
const PENDING_OPTOUT_KEY = 'compudopt_analytics_optout_pending';

class MixpanelService {
  constructor() {
    this.initialized = false;
    this.queue = []; // Queue events before initialization
  }

  hasPendingOptOut() {
    try {
      return localStorage.getItem(PENDING_OPTOUT_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  clearPendingOptOut() {
    try {
      localStorage.removeItem(PENDING_OPTOUT_KEY);
    } catch (e) {
      // Storage unavailable — nothing to clear
    }
  }

  /**
   * Initialize Mixpanel with privacy-focused configuration
   */
  initialize() {
    if (this.initialized) return;

    const token = MIXPANEL_CONFIG.token;

    if (!token) {
      // Silent fail in production - analytics just won't work
      if (MIXPANEL_CONFIG.debug) {
        console.warn('[MixpanelService] Token not configured - analytics disabled');
      }
      return;
    }

    try {
      mixpanel.init(token, {
        debug: MIXPANEL_CONFIG.debug,
        track_pageview: MIXPANEL_CONFIG.track_pageview,
        persistence: MIXPANEL_CONFIG.persistence,
        ignore_dnt: MIXPANEL_CONFIG.ignore_dnt,
        batch_requests: MIXPANEL_CONFIG.batch_requests,
        batch_size: MIXPANEL_CONFIG.batch_size,
        batch_flush_interval_ms: MIXPANEL_CONFIG.batch_flush_interval_ms,
        api_host: MIXPANEL_CONFIG.api_host,
        ip: MIXPANEL_CONFIG.ip,
        property_blacklist: MIXPANEL_CONFIG.property_blacklist,
      });

      this.initialized = true;

      // A decline made before init would otherwise be a no-op while the
      // declined user's queued events replay below — apply it first
      if (this.hasPendingOptOut()) {
        mixpanel.opt_out_tracking();
        this.queue = [];
        this.clearPendingOptOut();
      }

      this.registerSuperProperties();
      this.flushQueue();

      // Debug-only verification event — in production this fired on every
      // page load for every user, inflating event volumes
      if (MIXPANEL_CONFIG.debug) {
        mixpanel.track('Mixpanel Initialized', {
          timestamp: new Date().toISOString(),
          environment: process.env.NODE_ENV,
          version: process.env.REACT_APP_VERSION || '1.0.0',
        });
      }

      if (MIXPANEL_CONFIG.debug) {
        console.log('[MixpanelService] Initialized successfully');
        console.log('[MixpanelService] Token:', token.substring(0, 8) + '...');
      }
    } catch (error) {
      console.error('[MixpanelService] Initialization failed:', error);
    }
  }

  /**
   * Register super properties sent with every event
   */
  registerSuperProperties() {
    mixpanel.register({
      'App Version': process.env.REACT_APP_VERSION || '1.0.0',
      'Platform': 'web-helper',
      'Environment': process.env.NODE_ENV,
    });
  }

  /**
   * Flush queued events after initialization
   */
  flushQueue() {
    while (this.queue.length > 0) {
      const { method, args } = this.queue.shift();
      this[method](...args);
    }
  }

  /**
   * Queue or execute a method
   */
  queueOrExecute(method, args) {
    if (!this.initialized) {
      this.queue.push({ method, args });
      return;
    }
  }

  // ============================================
  // Identity Management
  // ============================================

  /**
   * Identify user by session ID (truncated for privacy)
   * @param {string} sessionId - Full session ID
   */
  identify(sessionId) {
    if (!this.initialized) {
      this.queueOrExecute('identify', [sessionId]);
      return;
    }

    const anonId = privacyAnonymizer.truncateSessionId(sessionId);
    mixpanel.identify(anonId);
  }

  /**
   * Reset user identity (for logout or session end)
   */
  reset() {
    if (!this.initialized) return;
    mixpanel.reset();
  }

  /**
   * Create an alias for the user
   * @param {string} alias - New alias
   */
  alias(alias) {
    if (!this.initialized) return;
    mixpanel.alias(alias);
  }

  // ============================================
  // Event Tracking
  // ============================================

  /**
   * Track an event with sanitized properties
   * @param {string} eventName - Name of the event
   * @param {Object} properties - Event properties
   */
  track(eventName, properties = {}) {
    if (!this.initialized) {
      this.queueOrExecute('track', [eventName, properties]);
      return;
    }

    const cleanProperties = this.sanitizeProperties(properties);

    mixpanel.track(eventName, {
      ...cleanProperties,
      timestamp: new Date().toISOString(),
    });

    if (MIXPANEL_CONFIG.debug) {
      console.log('[MixpanelService] Track:', eventName, cleanProperties);
    }
  }

  /**
   * Sanitize properties to remove PII
   * @param {Object} properties - Raw properties
   * @returns {Object} - Sanitized properties
   */
  sanitizeProperties(properties) {
    const sanitized = {};

    for (const [key, value] of Object.entries(properties)) {
      // Skip null/undefined
      if (value === null || value === undefined) {
        continue;
      }

      // Sanitize strings
      if (typeof value === 'string') {
        sanitized[key] = privacyAnonymizer.anonymizeText(value);
      }
      // Recursively sanitize objects
      else if (typeof value === 'object' && !Array.isArray(value)) {
        sanitized[key] = this.sanitizeProperties(value);
      }
      // Sanitize arrays — recurse into nested objects so PII-bearing
      // strings inside them are redacted too
      else if (Array.isArray(value)) {
        sanitized[key] = value.map(item => {
          if (typeof item === 'string') return privacyAnonymizer.anonymizeText(item);
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            return this.sanitizeProperties(item);
          }
          return item;
        });
      }
      // Pass through other types
      else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  // ============================================
  // User Properties
  // ============================================

  /**
   * Set user properties
   * @param {Object} properties - Properties to set
   */
  setUserProperties(properties) {
    if (!this.initialized) {
      this.queueOrExecute('setUserProperties', [properties]);
      return;
    }

    const cleanProperties = this.sanitizeProperties(properties);
    mixpanel.people.set(cleanProperties);
  }

  /**
   * Set user properties only if not already set
   * @param {Object} properties - Properties to set once
   */
  setUserPropertyOnce(properties) {
    if (!this.initialized) {
      this.queueOrExecute('setUserPropertyOnce', [properties]);
      return;
    }

    const cleanProperties = this.sanitizeProperties(properties);
    mixpanel.people.set_once(cleanProperties);
  }

  /**
   * Increment a numeric user property
   * @param {string} property - Property name
   * @param {number} amount - Amount to increment (default 1)
   */
  incrementUserProperty(property, amount = 1) {
    if (!this.initialized) {
      this.queueOrExecute('incrementUserProperty', [property, amount]);
      return;
    }

    mixpanel.people.increment(property, amount);
  }

  /**
   * Append to a list user property
   * @param {string} property - Property name
   * @param {*} value - Value to append
   */
  appendToUserProperty(property, value) {
    if (!this.initialized) return;
    mixpanel.people.append(property, value);
  }

  /**
   * Remove from a list user property
   * @param {string} property - Property name
   * @param {*} value - Value to remove
   */
  removeFromUserProperty(property, value) {
    if (!this.initialized) return;
    mixpanel.people.remove(property, value);
  }

  /**
   * Union values to a list property (adds unique)
   * @param {Object} properties - Object with list property names and values
   */
  unionUserProperty(properties) {
    if (!this.initialized) return;
    mixpanel.people.union(properties);
  }

  // ============================================
  // Time Tracking
  // ============================================

  /**
   * Start timing an event (call before event starts)
   * @param {string} eventName - Event to time
   */
  timeEvent(eventName) {
    if (!this.initialized) {
      this.queueOrExecute('timeEvent', [eventName]);
      return;
    }

    mixpanel.time_event(eventName);
  }

  // ============================================
  // Super Properties
  // ============================================

  /**
   * Register super properties (sent with all events)
   * @param {Object} properties - Properties to register
   */
  register(properties) {
    if (!this.initialized) return;
    const cleanProperties = this.sanitizeProperties(properties);
    mixpanel.register(cleanProperties);
  }

  /**
   * Register super properties once (only if not set)
   * @param {Object} properties - Properties to register
   */
  registerOnce(properties) {
    if (!this.initialized) return;
    const cleanProperties = this.sanitizeProperties(properties);
    mixpanel.register_once(cleanProperties);
  }

  /**
   * Unregister a super property
   * @param {string} property - Property name to remove
   */
  unregister(property) {
    if (!this.initialized) return;
    mixpanel.unregister(property);
  }

  // ============================================
  // Opt-out / Privacy Controls
  // ============================================

  /**
   * Opt user out of tracking
   */
  optOut() {
    if (!this.initialized) {
      // Honor the decline even before init — see PENDING_OPTOUT_KEY
      try {
        localStorage.setItem(PENDING_OPTOUT_KEY, 'true');
      } catch (e) {
        // Storage unavailable — decline holds for this page load only
      }
      this.queue = [];
      return;
    }
    mixpanel.opt_out_tracking();
  }

  /**
   * Opt user into tracking
   */
  optIn() {
    this.clearPendingOptOut();
    if (!this.initialized) return;
    mixpanel.opt_in_tracking();
  }

  /**
   * Check if user has opted out
   * @returns {boolean} - True if opted out
   */
  hasOptedOut() {
    if (!this.initialized) return this.hasPendingOptOut();
    return mixpanel.has_opted_out_tracking();
  }

  /**
   * Clear all super properties
   * NOTE: must never call mixpanel.clear_opt_in_out_tracking() here — that
   * clears the user's opt-out state (silently re-enabling tracking for
   * someone who declined), not super properties.
   */
  clearSuperProperties() {
    if (!this.initialized) return;
    ['App Version', 'Platform', 'Environment'].forEach((prop) => {
      mixpanel.unregister(prop);
    });
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Get the distinct ID for current user
   * @returns {string|null} - Distinct ID or null
   */
  getDistinctId() {
    if (!this.initialized) return null;
    return mixpanel.get_distinct_id();
  }

  /**
   * Check if Mixpanel is initialized
   * @returns {boolean} - Initialization status
   */
  isInitialized() {
    return this.initialized;
  }

  /**
   * Get the current super properties
   * @returns {Object|null} - Super properties or null
   */
  getSuperProperties() {
    if (!this.initialized) return null;
    return mixpanel.get_property('$super_properties');
  }
}

// Export singleton instance
const mixpanelService = new MixpanelService();
export default mixpanelService;
